import { useState } from "react";
import { useDebounce } from "@/hooks/useDebounce";
import { useNaturezasOperacao, useCstIcmsCombo, useCsosnCombo, useCstPisCofinsCombo } from "@/features/fiscal/hooks/useFiscal";
import { PaginacaoRodape } from "@/components/PaginacaoRodape";
import { type TamanhoPagina } from "@/hooks/usePaginacaoLocal";
import type { NaturezaOperacaoListagemDto, NaturezaOperacaoDto, SalvarNaturezaOperacaoRequest } from "@/features/fiscal/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CfopSearchCombobox } from "@/components/CfopSearchCombobox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, Search, FileText } from "lucide-react";
import { toast } from "sonner";

const EMPTY_FORM: SalvarNaturezaOperacaoRequest = {
  descricao: "", movimentaFinanceiro: true, movimentaEstoque: true, contaComoVenda: true,
  forcarCfop: false, isInativo: false,
  cstSaida: "", cstEntrada: "", csosn: "",
  cstPisCofinsaida: "", cstPisCofinsEntrada: "",
  cfopSaida: null, cfopSaidaNaoContrib: null, cfopSaidaUf: null,
  cfopSaidaUfNaoContrib: null, cfopSaidaExterior: null, cfopSaidaContaForaConta: null,
  cfopEntrada: null, cfopEntradaUf: null, cfopEntradaExterior: null,
  cfopSaidaStInterna: null, cfopSaidaStInterestadual: null,
  cfopEntradaStInterna: null, cfopEntradaStInterestadual: null,
  tipoNfDebito: 0, tipoNfCredito: 0,
};

function dtoToForm(d: NaturezaOperacaoDto): SalvarNaturezaOperacaoRequest {
  return {
    descricao: d.descricao ?? "",
    movimentaFinanceiro: d.movimentaFinanceiro,
    movimentaEstoque: d.movimentaEstoque,
    contaComoVenda: d.contaComoVenda ?? true,
    forcarCfop: d.forcarCfop,
    isInativo: d.isInativo,
    cstSaida: d.cstSaida ?? "",
    cstEntrada: d.cstEntrada ?? "",
    csosn: d.csosn ?? "",
    cstPisCofinsaida: d.cstPisCofinsaida ?? "",
    cstPisCofinsEntrada: d.cstPisCofinsEntrada ?? "",
    cfopSaida: d.cfopSaida,
    cfopSaidaNaoContrib: d.cfopSaidaNaoContrib,
    cfopSaidaUf: d.cfopSaidaUf,
    cfopSaidaUfNaoContrib: d.cfopSaidaUfNaoContrib,
    cfopSaidaExterior: d.cfopSaidaExterior,
    cfopSaidaContaForaConta: d.cfopSaidaContaForaConta,
    cfopEntrada: d.cfopEntrada,
    cfopEntradaUf: d.cfopEntradaUf,
    cfopEntradaExterior: d.cfopEntradaExterior,
    cfopSaidaStInterna: d.cfopSaidaStInterna,
    cfopSaidaStInterestadual: d.cfopSaidaStInterestadual,
    cfopEntradaStInterna: d.cfopEntradaStInterna,
    cfopEntradaStInterestadual: d.cfopEntradaStInterestadual,
    tipoNfDebito: d.tipoNfDebito,
    tipoNfCredito: d.tipoNfCredito,
  };
}

function CfopInput({ label, value, onChange }: {
  label: string;
  value: number | null;
  onChange: (v: number | null) => void;
}) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <CfopSearchCombobox value={value} onChange={onChange} />
    </div>
  );
}

function CodigoSelect({ label, value, options, onChange }: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
}) {
  // Preserva valor já salvo mesmo que não esteja na lista de combos
  const opts = value && !options.some(o => o.value === value)
    ? [{ value, label: value }, ...options]
    : options;
  return (
    <div className="space-y-1">
      <Label>{label}</Label>
      <Select value={value || "__none__"} onValueChange={v => onChange(v === "__none__" ? "" : v)}>
        <SelectTrigger className="font-mono"><SelectValue placeholder="Selecione..." /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">— Nenhum —</SelectItem>
          {opts.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

export default function NaturezaOperacao() {
  const [busca, setBusca] = useState("");
  const [pagina, setPagina] = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState<TamanhoPagina>(20);
  const buscaDebounced = useDebounce(busca, 400);
  const { query, obterPorId, criar, atualizar, excluir } = useNaturezasOperacao(buscaDebounced || undefined, pagina, tamanhoPagina);
  const itens: NaturezaOperacaoListagemDto[] = query.data?.itens ?? [];

  const totalItens = query.data?.total ?? 0;
  const primeiroItem = totalItens === 0 ? 0 : (pagina - 1) * tamanhoPagina + 1;
  const ultimoItem = Math.min(pagina * tamanhoPagina, totalItens);

  const [dialogOpen,  setDialogOpen]  = useState(false);
  const [excluirId,   setExcluirId]   = useState<number | null>(null);
  const [editingId,   setEditingId]   = useState<number | null>(null);
  const [loading,     setLoading]     = useState(false);
  const [form,        setForm]        = useState<SalvarNaturezaOperacaoRequest>(EMPTY_FORM);

  const set = <K extends keyof SalvarNaturezaOperacaoRequest>(k: K, v: SalvarNaturezaOperacaoRequest[K]) =>
    setForm(f => ({ ...f, [k]: v }));

  // Combos de CST/CSOSN (substituem texto livre)
  const cstIcmsCombo = useCstIcmsCombo();
  const csosnCombo   = useCsosnCombo();
  const pisCombo     = useCstPisCofinsCombo();

  const icmsOpts = (cstIcmsCombo.data ?? [])
    .filter(c => c.cst && c.cst.length <= 2)   // CST ICMS tradicional (2 dígitos); IBS/CBS tem 3
    .map(c => ({ value: c.cst!, label: `${c.cst} — ${c.descricao ?? ""}` }));
  const csosnOpts = (csosnCombo.data ?? [])
    .map(c => ({ value: String(c.codigoCsosn), label: `${c.codigoCsosn} — ${c.descricao ?? ""}` }));
  const pisOpts = Array.from(
    new Map((pisCombo.data ?? []).filter(c => c.cst).map(c => [c.cst!, c])).values()
  ).map(c => ({ value: c.cst!, label: `${c.cst} — ${c.descricao ?? ""}` }));

  const openNew = () => { setEditingId(null); setForm(EMPTY_FORM); setDialogOpen(true); };

  const openEdit = async (id: number) => {
    setLoading(true);
    try {
      const dto = await obterPorId(id);
      setEditingId(id);
      setForm(dtoToForm(dto));
      setDialogOpen(true);
    } catch { toast.error("Não foi possível carregar a natureza de operação."); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.descricao.trim()) { toast.error("Descrição é obrigatória."); return; }
    try {
      if (editingId) {
        await atualizar.mutateAsync({ id: editingId, req: form });
        toast.success("Natureza de operação atualizada.");
      } else {
        await criar.mutateAsync(form);
        toast.success("Natureza de operação criada.");
      }
      setDialogOpen(false);
    } catch { /* global toast */ }
  };

  const handleExcluir = async () => {
    if (!excluirId) return;
    try { await excluir.mutateAsync(excluirId); toast.success("Excluída com sucesso."); }
    catch { /* global toast */ }
    setExcluirId(null);
  };

  const isSaving = criar.isPending || atualizar.isPending;

  if (query.isLoading) return <div className="p-6">Carregando...</div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="h-6 w-6" /> Natureza de Operação
          </h1>
          <p className="text-muted-foreground">Tipos de operação para emissão de documentos fiscais</p>
        </div>
        <Button onClick={openNew}><Plus className="h-4 w-4 mr-2" /> Nova</Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Buscar..." className="pl-9" value={busca} onChange={e => { setBusca(e.target.value); setPagina(1); }} />
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Descrição</TableHead>
              <TableHead className="text-center">Financeiro</TableHead>
              <TableHead className="text-center">Estoque</TableHead>
              <TableHead className="text-center" title="A nota com esta natureza entra nos relatórios de vendas">Vendas</TableHead>
              <TableHead className="text-center">Status</TableHead>
              <TableHead className="w-[90px]">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {itens.map(n => (
              <TableRow key={n.id}>
                <TableCell className="font-medium">{n.descricao ?? "—"}</TableCell>
                <TableCell className="text-center">
                  <Badge variant={n.movimentaFinanceiro ? "success" : "secondary"}>
                    {n.movimentaFinanceiro ? "Sim" : "Não"}
                  </Badge>
                </TableCell>
                <TableCell className="text-center">
                  <Badge variant={n.movimentaEstoque ? "success" : "secondary"}>
                    {n.movimentaEstoque ? "Sim" : "Não"}
                  </Badge>
                </TableCell>
                <TableCell className="text-center">
                  <Badge variant={n.contaComoVenda !== false ? "success" : "secondary"}>
                    {n.contaComoVenda !== false ? "Sim" : "Não"}
                  </Badge>
                </TableCell>
                <TableCell className="text-center">
                  <Badge variant={n.isInativo ? "secondary" : "success"}>
                    {n.isInativo ? "Inativa" : "Ativa"}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(n.id)} disabled={loading}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setExcluirId(n.id)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {itens.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  Nenhuma natureza de operação cadastrada.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Paginação */}
      {query.data && (
        <PaginacaoRodape
          pagina={pagina}
          totalPaginas={query.data.totalPaginas}
          totalItens={totalItens}
          primeiroItem={primeiroItem}
          ultimoItem={ultimoItem}
          tamanhoPagina={tamanhoPagina}
          onPaginaChange={setPagina}
          onTamanhoPaginaChange={setTamanhoPagina}
        />
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Editar Natureza de Operação" : "Nova Natureza de Operação"}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit}>
            <Tabs defaultValue="dados">
              <TabsList className="mb-4">
                <TabsTrigger value="dados">Dados</TabsTrigger>
                <TabsTrigger value="cfops-saida">CFOPs Saída</TabsTrigger>
                <TabsTrigger value="cfops-entrada">CFOPs Entrada</TabsTrigger>
                <TabsTrigger value="csts">CSTs</TabsTrigger>
              </TabsList>

              {/* ── Dados ─────────────────────────────────────────── */}
              <TabsContent value="dados" className="space-y-4">
                <div className="space-y-1">
                  <Label htmlFor="descricao">Descrição *</Label>
                  <Input
                    id="descricao"
                    maxLength={59}
                    value={form.descricao}
                    onChange={e => set("descricao", e.target.value)}
                    required
                  />
                </div>
                <div className="flex flex-wrap gap-6 pt-2">
                  {[
                    { id: "movimentaFinanceiro", label: "Movimenta Financeiro", field: "movimentaFinanceiro" as const },
                    { id: "movimentaEstoque",    label: "Movimenta Estoque",    field: "movimentaEstoque"    as const },
                    { id: "contaComoVenda",      label: "Conta como venda nos relatórios", field: "contaComoVenda" as const },
                    { id: "forcarCfop",          label: "Forçar CFOP",          field: "forcarCfop"          as const },
                    { id: "isInativo",           label: "Inativa",              field: "isInativo"           as const },
                  ].map(({ id, label, field }) => (
                    <div key={id} className="flex items-center gap-2">
                      <Switch id={id} checked={form[field]} onCheckedChange={v => set(field, v)} />
                      <Label htmlFor={id}>{label}</Label>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  Desligue "Conta como venda" em bonificação, remessa, transferência, devolução e outras operações que
                  não são venda: a nota com esta natureza continua nos relatórios fiscais, mas sai dos relatórios de vendas.
                </p>
              </TabsContent>

              {/* ── CFOPs Saída ────────────────────────────────────── */}
              <TabsContent value="cfops-saida" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <CfopInput label="Saída — Contrib. mesmo estado"       value={form.cfopSaida}              onChange={v => set("cfopSaida", v)} />
                  <CfopInput label="Saída — Não Contrib. mesmo estado"   value={form.cfopSaidaNaoContrib}    onChange={v => set("cfopSaidaNaoContrib", v)} />
                  <CfopInput label="Saída — Contrib. outro estado"       value={form.cfopSaidaUf}            onChange={v => set("cfopSaidaUf", v)} />
                  <CfopInput label="Saída — Não Contrib. outro estado"   value={form.cfopSaidaUfNaoContrib}  onChange={v => set("cfopSaidaUfNaoContrib", v)} />
                  <CfopInput label="Saída — Exterior"                    value={form.cfopSaidaExterior}      onChange={v => set("cfopSaidaExterior", v)} />
                  <CfopInput label="Saída — Conta e Ordem"               value={form.cfopSaidaContaForaConta} onChange={v => set("cfopSaidaContaForaConta", v)} />
                  <CfopInput label="Dev. Trib. — Interna"               value={form.cfopSaidaStInterna}     onChange={v => set("cfopSaidaStInterna", v)} />
                  <CfopInput label="Dev. Trib. — Interestadual"         value={form.cfopSaidaStInterestadual} onChange={v => set("cfopSaidaStInterestadual", v)} />
                </div>
              </TabsContent>

              {/* ── CFOPs Entrada ──────────────────────────────────── */}
              <TabsContent value="cfops-entrada" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <CfopInput label="Entrada — mesmo estado"    value={form.cfopEntrada}         onChange={v => set("cfopEntrada", v)} />
                  <CfopInput label="Entrada — outro estado"    value={form.cfopEntradaUf}        onChange={v => set("cfopEntradaUf", v)} />
                  <CfopInput label="Entrada — Exterior"        value={form.cfopEntradaExterior}  onChange={v => set("cfopEntradaExterior", v)} />
                  <div /> {/* spacer */}
                  <CfopInput label="Dev. ST — Interna"        value={form.cfopEntradaStInterna}      onChange={v => set("cfopEntradaStInterna", v)} />
                  <CfopInput label="Dev. ST — Interestadual"  value={form.cfopEntradaStInterestadual} onChange={v => set("cfopEntradaStInterestadual", v)} />
                </div>
              </TabsContent>

              {/* ── CSTs ───────────────────────────────────────────── */}
              <TabsContent value="csts" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <CodigoSelect label="CST ICMS — Saída"   value={form.cstSaida}   options={icmsOpts}  onChange={v => set("cstSaida", v)} />
                  <CodigoSelect label="CST ICMS — Entrada" value={form.cstEntrada} options={icmsOpts}  onChange={v => set("cstEntrada", v)} />
                  <CodigoSelect label="CSOSN"              value={form.csosn}      options={csosnOpts} onChange={v => set("csosn", v)} />
                  <div />
                  <CodigoSelect label="CST PIS/COFINS — Saída"   value={form.cstPisCofinsaida}    options={pisOpts} onChange={v => set("cstPisCofinsaida", v)} />
                  <CodigoSelect label="CST PIS/COFINS — Entrada" value={form.cstPisCofinsEntrada} options={pisOpts} onChange={v => set("cstPisCofinsEntrada", v)} />
                </div>
              </TabsContent>
            </Tabs>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={isSaving}>{isSaving ? "Salvando..." : "Salvar"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!excluirId} onOpenChange={() => setExcluirId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir natureza de operação</AlertDialogTitle>
            <AlertDialogDescription>Esta ação não pode ser desfeita. Deseja continuar?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleExcluir} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
